import { EMT_PAGE_NAMES, HD_URL_L } from '../../constants';
import { getCurrentPromoImageSrc } from '../../helpers/pps-helpers';
import { generateBreadcrumbList } from '../../utils/generate-breadcrumb-list';

/**
 * Generates the title for a local city Home Depot page.
 *
 * @param {Object} params - Parameters for generating the title.
 * @param {string} params.city - The city name.
 * @param {string} params.state - The state abbreviation.
 * @returns {string} The generated title for the local city page.
 */
export const generateLocalCityTitle = ({ city, state }) => {
  if (!city || !state) {
    return 'The Home Depot';
  }

  return `Find Everything You Need at The Home Depot - ${city}, ${state}`;
};

/**
 * Generates the meta description for a local city Home Depot page.
 *
 * @param {Object} params - Parameters for generating the description.
 * @param {string} params.city - The city name.
 * @param {string} params.state - The state abbreviation.
 * @returns {string} The generated description for the local city page.
 */
export const generateLocalCityDesc = ({ city, state }) => {
  if (!city || !state) {
    return 'The Home Depot';
  }

  return 'Looking for the local Home Depot in your city? '
    + `Find everything you need in one place at The Home Depot in ${city}, ${state}.`;
};

/**
 * Generates the canonical URL for a local city Home Depot page.
 *
 * @param {Object} params - Parameters for generating the canonical URL.
 * @param {string} params.city - The city name.
 * @param {string} params.state - The state abbreviation.
 * @param {string|number} params.guid - The unique guid for the location.
 * @returns {string} The canonical URL for the local city page.
 */
export const generateLocalCityCanonical = ({ city, state, guid }) => {
  const formattedCity = city.trim().split(' ').join('-');

  return `https://www.homedepot.com/l/${state}/${formattedCity}/${guid}`;
};

/**
 * Generates an array of preload image objects for local city promotional images.
 * Returns an array with a single image object if a promotion image is available for the local city page,
 * otherwise returns null.
 *
 * @param {Object} storeFinderPromotions - An object containing promotion data with image URLs, typically from emtData.
 * @returns {Array|null} An array with a single preload image object, or null if no promotion image is available.
 */
export const getLocalCityPreloadImages = (storeFinderPromotions) => {
  const promotionImgUrl = getCurrentPromoImageSrc(
    storeFinderPromotions,
    EMT_PAGE_NAMES.LOCAL_CITY
  );
  if (promotionImgUrl) {
    return [{ href: promotionImgUrl }];
  }
  return null;
};

/**
 * Generates an array of schema objects for use in generating local city structured data script tags.
 * If `emtSchema` is provided, it will be used as the only schema in the array.
 * Otherwise, a default breadcrumb schema is generated using the provided city, state, and guid.
 *
 * @param {Object} params - Parameters for generating schemas.
 * @param {string} [params.emtSchema] - A string containing the JSON-LD schema content for the local city page.
 * @param {string} params.city - The city name.
 * @param {string} params.state - The state abbreviation.
 * @param {string|number} params.guid - The unique guid for the location.
 * @returns {Array} An array of schema objects, each with a `content` (string) and `id` (string).
 */
export const getLocalCitySchemaTags = ({ emtSchema, city, state, guid }) => {
  if (emtSchema) {
    return [{ content: emtSchema, id: 'localCityStructuredData' }];
  }

  const breadCrumbItems = [
    { name: 'Store Locator', url: 'store-locator' },
    { name: 'Store Directory', url: 'storeDirectory' }
  ];
  if (city && state && guid) {
    breadCrumbItems.push(
      { name: state, url: `${state}` },
      { name: city, url: `${state}/${city.split(' ').join('-')}/${guid}` }
    );
  }

  return ([{
    content: generateBreadcrumbList(breadCrumbItems, { urlPrefix: HD_URL_L }),
    id: 'storeDetailStructuredBreadcrumb'
  }]);
};